import { Dialog, DialogPanel, Transition } from "@headlessui/react";
import { withCacheBuster } from "../../utils/cacheBuster";
import * as cadviewer from "cadviewer";
import classNames from "classnames";
import { debounce } from "lodash";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useLocation } from "react-router-dom";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { Plan, PlanDetail } from "../../types";
import { PlanType, SearchPlanType, Space } from "../../types/plan";
import cleanKeys from "../../utils/object";
import QuickPrintPdf from "../QuickPrintPdf";
import {
  isTabletOrMobile,
  useWindowSize,
} from "../RightAction/RightAction.component";
import { TiledViewsWindow, TiledViewsData } from "../TiledViews";

interface PlanJsonRightPanelProps {
  open: boolean;
  setOpen: any;
}

// Utility hook for debouncing
const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler); // Clean up on unmount or when value changes
  }, [value, delay]);
  return debouncedValue;
};

function PlanSpaceList(props: {
  expandedPlanSpaces: { [p: string]: boolean };
  plan: Plan;
  width: number;
  planDetail: PlanDetail;
  foundProperties: any;
  element: (space: Space) => React.JSX.Element;
  onClick: () => void;
}) {
  // Ref to attach to the component's root element
  const componentRef = useRef<HTMLDivElement>(null);

  // State to store the component's height
  const [componentHeight, setComponentHeight] = useState(0);

  // Function to measure and update the component's height
  const measureHeight = () => {
    if (componentRef.current) {
      const height = componentRef.current.offsetHeight;
      setComponentHeight(height);
    }
  };

  // Use effect to measure height on mount and when content changes
  useEffect(() => {
    // Initial measurement
    measureHeight();

    // Optional: Measure height on window resize
    window.addEventListener("resize", measureHeight);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("resize", measureHeight);
    };
  }, [props.width]);

  return (
    <div className={"flex items-start my-1 mt-4 border-t border-gray-200 pt-1"}>
      <div
        className={`w-full flex-1 transition-all duration-300 h-auto overflow-hidden ${props.expandedPlanSpaces[
          props.plan.propertyNumber + "-" + props.planDetail.planName
        ]
          ? ""
          : "max-h-9"
          }`}
      >
        <nav
          ref={componentRef}
          aria-label="Tabs"
          className="-mb-px gap-x-2 flex flex-wrap"
        >
          {(
            props.foundProperties[props.plan.propertyNumber][
              props.planDetail.planName
            ].spaces ?? []
          ).map(props.element)}
        </nav>
      </div>
      {/*	 button arrow for expand and close list of properties	*/}
      {componentHeight > 36 && (
        <div
          className="flex items-center justify-center w-9 h-9 bg-gray-300 text-gray-500 cursor-pointer"
          onClick={props.onClick}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`w-6 h-6 transition-all duration-200 ${props.expandedPlanSpaces[
              props.plan.propertyNumber + "-" + props.planDetail.planName
            ]
              ? "transform rotate-180"
              : ""
              }`}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

const PlanJsonRightPanel = ({ open, setOpen }: PlanJsonRightPanelProps) => {
  const { config } = useConfig();
  let {
    globalApplicationFilesLocation,
    ServerBackEndUrl,
    postFixServerToken,
    globalApplicationPlans,
    globalApplicationSearch,
    propertySVGFilesLocation,
    defautlPropertyToLoad,
  } = config;
  const {
    setSelectedPlan,
    setSelectedPlanDetail,
    setSelectedSpaceObjectID,
    setCurrentDrawingPath,
    planTypes,
    currentPlanType,
    setCurrentPlanType,
    views,
    fetchViewJson,
    setMultiSelectArray,
    queryStoreId,
    signalDrawingLoadStart,
  } = useContext(CadviewerContext);

  const [alreadyLoadDefaultProperty, setAlreadyLoadDefaultProperty] =
    useState<boolean>(false);
  const [plans, setPlans] = useState<PlanType[]>([]);
  const [searchPlans, setSearchPlans] = useState<SearchPlanType[]>([]);
  const [selected, setSelected] = useState<Plan | null>(null);
  const divRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [mountedQueryFile, setMountedQueryFile] = useState<boolean>(false);
  const [openQuickPdfPrint, setOpenQuickPdfPrint] = useState<{
    open: boolean;
    propertyName: string;
    plan: Plan | undefined;
    propertyNumber: string;
    plans: PlanDetail[];
  }>({
    open: false,
    propertyName: "",
    propertyNumber: "",
    plans: [],
    plan: undefined,
  });

  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [foundProperties, setFoundProperties] = useState<any>({});

  // Tiled Views draggable window
  const [tiledViewsOpen, setTiledViewsOpen] = useState<boolean>(false);
  const [activeTiledFloorName, setActiveTiledFloorName] = useState<
    string | undefined
  >(undefined);
  const [tiledViewsData, setTiledViewsData] = useState<TiledViewsData | null>(
    null
  );
  const [tiledViewsLoading, setTiledViewsLoading] = useState<boolean>(false);
  const [tiledViewsError, setTiledViewsError] = useState<string | null>(null);
  // Property whose tiled views are currently shown (scopes floor lookup).
  const [tiledViewsPlan, setTiledViewsPlan] = useState<Plan | null>(null);
  // Property number of the dataset currently loaded, to avoid refetching.
  const loadedTiledPropertyRef = useRef<string | undefined>(undefined);

  const [error, setError] = useState<string | null>(null);
  const [expandedPlanSpaces, setExpandedPlanSpaces] = useState<{
    [key: string]: boolean;
  }>({});

  const useQuery = () => {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
  };

  let query = useQuery();

  const bpretailSourceFile = useMemo(
    () => query.get("bpretailSourceFile"),
    [query]
  );
  const bpretailSearchItem = useMemo(
    () => query.get("bpretailSearchItem"),
    [query]
  );

  // Query params to bypass defautlPropertyToLoad config
  const queryPropertyNumber = useMemo(
    () => query.get("propertyNumber"),
    [query]
  );
  const queryPlanName = useMemo(
    () => query.get("planName"),
    [query]
  );

  const fetchPlanJson = useCallback(async () => {
    try {
      if (globalApplicationFilesLocation === "") return;

      const url = withCacheBuster(globalApplicationFilesLocation + "" + globalApplicationPlans);

      var endpoint = "loadjsonpostfix/proxy";
      if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) !== "/") {
        endpoint = "/" + endpoint;
      }

      let headers = undefined;
      if (postFixServerToken) {
        headers = {
          "Content-Type": "application/json",
        };
      }

      if (config.bearerToken) {
        headers = {
          ...(headers ?? {}),
          Authorization: `${config.bearerToken}`,
        };
      }

      const response = await fetch(
        postFixServerToken ? ServerBackEndUrl + endpoint : url,
        {
          method: postFixServerToken ? "POST" : "GET",
          headers,
          body: postFixServerToken ? JSON.stringify({ url: url }) : undefined,
          redirect: "follow",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setError(null);

      setPlans(cleanKeys(data.data) || []);
    } catch (error: any) {
      setError(error.message);
    }
  }, [
    globalApplicationFilesLocation,
    globalApplicationPlans,
    ServerBackEndUrl,
    postFixServerToken,
    config.bearerToken,
  ]);

  const fetchSearchJson = useCallback(async () => {
    try {
      if (globalApplicationFilesLocation === "") return;

      const url = withCacheBuster(globalApplicationFilesLocation + "" + globalApplicationSearch);

      var endpoint = "loadjsonpostfix/proxy";
      if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) === "/") {
      } else {
        endpoint = "/" + endpoint;
      }

      let headers = undefined;
      if (postFixServerToken) {
        headers = {
          "Content-Type": "application/json",
        };
      }

      if (config.bearerToken) {
        headers = {
          ...(headers ?? {}),
          Authorization: `${config.bearerToken}`,
        };
      }

      const response = await fetch(
        postFixServerToken ? ServerBackEndUrl + endpoint : url,
        {
          method: postFixServerToken ? "POST" : "GET",
          headers,
          body: postFixServerToken ? JSON.stringify({ url: url }) : undefined,
          redirect: "follow",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      setSearchPlans(
        (cleanKeys(data.data) || []).map((searchPlan: SearchPlanType) => {
          return searchPlan;
        })
      );
    } catch (error: any) { }
  }, [
    globalApplicationFilesLocation,
    globalApplicationSearch,
    ServerBackEndUrl,
    postFixServerToken,
    config.bearerToken,
  ]);

  // Fetch the tiled-views dataset from the same place the floor files live
  // (the "corporate" folder under propertyJSONFilesLocation), using the exact
  // same proxy/token-aware mechanism as fetchPlanJson. The file name is derived
  // from the selected property's number so several properties are supported.
  const fetchTiledViewsJson = useCallback(async (propertyNumber: string) => {
    const pattern =
      config.tiledViewsFilePattern || "corporate/{propertyNumber}-tiled-view.json";
    const fileName = pattern.replace(/\{propertyNumber\}/g, propertyNumber);
    try {
      setTiledViewsLoading(true);
      setTiledViewsError(null);

      // todo remove the replace when the json files are in the right location
      const url = (config.propertyJSONFilesLocation + "" + fileName).replace(
        "/v2/json/property/retail/",
        "/json/property/"
      );

      var endpoint = "loadjsonpostfix/proxy";
      if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) !== "/") {
        endpoint = "/" + endpoint;
      }

      let headers: Record<string, string> | undefined = undefined;
      if (postFixServerToken) {
        headers = {
          "Content-Type": "application/json",
        };
      }

      if (config.bearerToken) {
        headers = {
          ...(headers ?? {}),
          Authorization: `${config.bearerToken}`,
        };
      }

      const response = await fetch(
        postFixServerToken ? ServerBackEndUrl + endpoint : url,
        {
          method: postFixServerToken ? "POST" : "GET",
          headers,
          body: postFixServerToken ? JSON.stringify({ url: url }) : undefined,
          redirect: "follow",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      // Support both raw datasets and proxy-wrapped ({ data: ... }) responses.
      const payload = (data && data.views ? data : data?.data) as TiledViewsData;
      if (!payload || !Array.isArray(payload.views)) {
        throw new Error(`No tiled-views data for property ${propertyNumber}`);
      }
      setTiledViewsData(payload);
      loadedTiledPropertyRef.current = propertyNumber;
    } catch (error: any) {
      setTiledViewsData(null);
      loadedTiledPropertyRef.current = undefined;
      setTiledViewsError(error.message || "Failed to load tiled views");
    } finally {
      setTiledViewsLoading(false);
    }
  }, [
    config.tiledViewsFilePattern,
    config.propertyJSONFilesLocation,
    ServerBackEndUrl,
    postFixServerToken,
    config.bearerToken,
  ]);

  // Open the tiled-views window for a given property, (re)fetching its dataset
  // when a different property than the currently-loaded one is requested.
  const handleOpenTiledViews = useCallback(
    (plan: Plan) => {
      setTiledViewsPlan(plan);
      setActiveTiledFloorName(undefined);
      setTiledViewsOpen(true);
      if (loadedTiledPropertyRef.current !== plan.propertyNumber) {
        fetchTiledViewsJson(plan.propertyNumber);
      }
    },
    [fetchTiledViewsJson]
  );

  const selectFloorPlan = useCallback(
    async (plan: Plan, planDetail: PlanDetail, space: Space | undefined) => {
      console.log("selectFloorPlan", plan, planDetail, space);
      const svgFileLink = (
        propertySVGFilesLocation + planDetail.svgFileName
      ).replace("/v2/svg/retail/", "/svg/");

      // 10.44.3
      cadviewer.cvjs_ClearDrawing("floorPlan");

      cadviewer.cvjs_setZoomImageWallpaper(false); // 10.30.2

      console.log("[DeepLink] selectFloorPlan — signalDrawingLoadStart(), loading:", svgFileLink);
      signalDrawingLoadStart();
      // 10.36.6 - we block off here
      cadviewer.cvjs_LoadDrawing("floorPlan", svgFileLink);

      setCurrentDrawingPath(
        svgFileLink +
        `${space
          ? "?spaceNumber=" +
          space.spaceNumber.replaceAll(".", "_").replaceAll(" ", "_")
          : ""
        }`
      );
      setSelectedPlan(plan);
      setSelectedPlanDetail(planDetail);
      setSelectedSpaceObjectID(undefined);
      setMultiSelectArray([]);
      setOpen(false);
    },
    [
      propertySVGFilesLocation,
      setSelectedPlan,
      setOpen,
      setCurrentDrawingPath,
      setSelectedSpaceObjectID,
      setMultiSelectArray,
      setSelectedPlanDetail,
      signalDrawingLoadStart,
    ]
  );

  // Load a floor selected from the Tiled Views window by matching its floor /
  // plan name against the already-loaded plans. Reuses selectFloorPlan so the
  // SVG loads exactly like a normal property-panel selection. The draggable
  // window stays open (its state is independent of this panel's `open`).
  const handleSelectFloorByName = useCallback(
    (floorName: string, planName: string) => {
      setActiveTiledFloorName(floorName);

      let matchByFloor: { plan: Plan; planDetail: PlanDetail } | undefined;
      let matchByPlan: { plan: Plan; planDetail: PlanDetail } | undefined;

      // Prefer the property the tiled-views window was opened for, then fall
      // back to scanning every loaded property.
      const scopedProperties: Plan[] = [];
      if (tiledViewsPlan) scopedProperties.push(tiledViewsPlan);
      plans.forEach((planType) =>
        planType.properties.forEach((plan) => {
          if (plan.propertyNumber !== tiledViewsPlan?.propertyNumber) {
            scopedProperties.push(plan);
          }
        })
      );

      for (const plan of scopedProperties) {
        for (const planDetail of plan.plans) {
          if (planDetail.floorName === floorName) {
            matchByFloor = { plan, planDetail };
          }
          if (!matchByPlan && planDetail.planName === planName) {
            matchByPlan = { plan, planDetail };
          }
        }
        if (matchByFloor) break;
      }

      const match = matchByFloor ?? matchByPlan;
      if (!match) {
        console.warn(
          `[TiledViews] No loaded plan matches floor "${floorName}" (planName "${planName}").`
        );
        return;
      }

      selectFloorPlan(match.plan, match.planDetail, undefined);
    },
    [plans, tiledViewsPlan, selectFloorPlan]
  );

  useEffect(() => {
    const cvjs_customCommand_20 = () => {
      setOpen(true);
      cadviewer.cvjs_hideOnlyPop();
    };

    cadviewer.cvjs_setCallbackMethod(
      "cvjs_customCommand_20",
      cvjs_customCommand_20
    );
  }, [cadviewer]);

  useEffect(() => {
    fetchPlanJson().then((r) => console.log("fetchPlanJson", r));
    fetchSearchJson().then((r) => console.log("fetchSearchJson", r));
    
    const handleUpdate = () => {
      fetchPlanJson();
      fetchSearchJson();
    };
    window.addEventListener("applicationFilesUpdated", handleUpdate);
    return () => window.removeEventListener("applicationFilesUpdated", handleUpdate);
  }, [fetchPlanJson, open, fetchSearchJson]);

  const loadPropertyFromQueryParams = useCallback(async () => {
    if (views.length === 0) {
      await fetchViewJson();
    }
    if (plans.length === 0) {
      await fetchPlanJson();
    }
    if (plans.length === 0 || views.length === 0) {
      return;
    }
    let bpretailSourceFileTrimmed = (bpretailSourceFile ?? "").replace(
      "/v3/svg/",
      ""
    );
    // if first character is a slash, remove it
    if (bpretailSourceFileTrimmed.charAt(0) === "/") {
      bpretailSourceFileTrimmed = bpretailSourceFileTrimmed.substring(1);
    }
    const svgFileLink = propertySVGFilesLocation + bpretailSourceFileTrimmed;
    for (let i = 0; i < plans.length; i++) {
      for (let j = 0; j < plans[i].properties.length; j++) {
        const plan = plans[i].properties[j];
        for (let k = 0; k < plan.plans.length; k++) {
          const planDetail = plan.plans[k];
          if (planDetail.svgFileName === bpretailSourceFileTrimmed) {
            selectFloorPlan(plan, planDetail, undefined);
            setAlreadyLoadDefaultProperty(true);
            break;
          }
        }
      }
    }

    if (bpretailSearchItem)
      setCurrentDrawingPath(
        svgFileLink +
        `${bpretailSearchItem
          ? "?spaceNumber=" + bpretailSearchItem.replaceAll(".", "_")
          : ""
        }`
      );
    setMountedQueryFile(true);
  }, [
    views.length,
    plans,
    bpretailSourceFile,
    propertySVGFilesLocation,
    bpretailSearchItem,
    setCurrentDrawingPath,
    fetchViewJson,
    fetchPlanJson,
    selectFloorPlan,
  ]);

  useEffect(() => {
    if (!bpretailSourceFile || mountedQueryFile) return;
    loadPropertyFromQueryParams().then((r) => r);
  }, [
    bpretailSourceFile,
    bpretailSearchItem,
    mountedQueryFile,
    plans,
    views,
    loadPropertyFromQueryParams,
  ]);

  const minimunSearchKeyworkLength = 3;

  const debouncedSearchKeyword = useDebounce(searchKeyword, 100); // 100ms debounce

  useEffect(() => {
    // Pre-process searchKeyword once to avoid multiple .toLowerCase() calls
    const trimmedKeyword = debouncedSearchKeyword.trim().toLowerCase();

    // Exit early if the keyword is too short
    if (trimmedKeyword.length < minimunSearchKeyworkLength) {
      if (Object.keys(foundProperties).length !== 0) {
        setFoundProperties({});
      }
      return; // No need to continue processing
    }

    // Initialize a temporary object to hold found properties
    const tmpFoundProperties: any = {};

    // Find plans matching the current plan type
    const currentPlan = searchPlans.find(
      (planType) => planType.planType === currentPlanType
    );

    // Exit if no matching planType is found
    if (!currentPlan) return;

    // Iterate over the plans and filter based on searchKeyword
    currentPlan.plans.forEach((plan) => {
      // Avoid repetitive trimming/lowering in each check
      const propertyMatches =
        plan.propertyName.toLowerCase().includes(trimmedKeyword) ||
        plan.propertyNumber.toLowerCase().includes(trimmedKeyword);
      const planMatches =
        plan.planName.toLowerCase().includes(trimmedKeyword) ||
        plan.planDisplayName.toLowerCase().includes(trimmedKeyword);
      const spacesMatches = plan.spaces.filter(
        (space) =>
          space.spaceHeader.toLowerCase().includes(trimmedKeyword) ||
          space.spaceNumber.toLowerCase().includes(trimmedKeyword)
      );

      // If any match is found, add to the tmpFoundProperties object
      if (propertyMatches || planMatches || spacesMatches.length > 0) {
        if (!tmpFoundProperties[plan.propertyNumber]) {
          tmpFoundProperties[plan.propertyNumber] = {};
        }
        tmpFoundProperties[plan.propertyNumber][plan.planName] = {
          spaces: spacesMatches,
        };
      }
    });

    // Update foundProperties state with the filtered results
    setFoundProperties(tmpFoundProperties);
  }, [
    debouncedSearchKeyword,
    searchPlans,
    setFoundProperties,
    currentPlanType,
  ]);

  useEffect(() => {
    const node = divRef.current;
    if (!node) return;

    let rafId: number | null = null;

    // The state update is deferred to the next frame and only applied when the
    // rounded width actually changed. Updating the layout from inside the
    // observation pass is what triggers "ResizeObserver loop completed with
    // undelivered notifications".
    const handleResize = debounce((entries: any) => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        for (let entry of entries) {
          const nextWidth = Math.round(entry.contentRect.width);
          setWidth((prev) => (prev === nextWidth ? prev : nextWidth));
        }
      });
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);

    try {
      resizeObserver.observe(node);
    } catch (err) {
      console.log("error resizeObserver:" + err);
    }

    // Cleanup
    return () => {
      handleResize.cancel();
      if (rafId !== null) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [divRef.current]);

  useEffect(() => {
    setSelected(null);
  }, [searchKeyword]);

  useEffect(() => {
    if (!open) {
      setSearchKeyword("");
    }
  }, [open]);

  useEffect(() => {
    // Query params take priority over config
    const effectivePropertyNumber = queryPropertyNumber || defautlPropertyToLoad.propertyNumber;
    const effectivePlanName = queryPlanName || defautlPropertyToLoad.planName;

    if (
      !alreadyLoadDefaultProperty &&
      effectivePropertyNumber &&
      effectivePlanName &&
      !bpretailSourceFile
    ) {
      for (let planList of plans) {
        const plan = planList.properties.find(
          (plan) => plan.propertyNumber === effectivePropertyNumber
        );
        const planDetail = plan?.plans.find(
          (planDetail) => planDetail.planName === effectivePlanName
        );
        if (plan && planDetail) {
          selectFloorPlan(plan, planDetail, undefined);
          setAlreadyLoadDefaultProperty(true);

          // If queryStoreId is provided, highlight the specified space
          if (queryStoreId) {
            const svgFileLink = (
              propertySVGFilesLocation + planDetail.svgFileName
            ).replace("/v2/svg/retail/", "/svg/");
            setCurrentDrawingPath(
              svgFileLink +
              "?spaceNumber=" + queryStoreId.replaceAll(".", "_").replaceAll(" ", "_")
            );
          }
          break;
        }
      }
    }
  }, [
    bpretailSourceFile,
    defautlPropertyToLoad,
    queryPropertyNumber,
    queryPlanName,
    queryStoreId,
    propertySVGFilesLocation,
    setCurrentDrawingPath,
    alreadyLoadDefaultProperty,
    plans,
    selectFloorPlan,
  ]);

  const { width: windowWidth } = useWindowSize();
  const isMobileOrTablet = useMemo(
    () => isTabletOrMobile(windowWidth),
    [windowWidth]
  );

  const renderPanelContent = () => {
    return (
      <Panel
        order={config.actionPanelSide === "left" ? 1 : 2}
        defaultSize={config.rightPanelDefaultWidth}
        className="relative"
      >
        <div className="absolute right-2 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
          <button
            type="button"
            className="relative rounded-md text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
            onClick={() => setOpen(false)}
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close panel</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex h-full flex-col overflow-y-scroll w-full overflow-x-hidden bg-white shadow-xl">
          <div className="bg-primary-700 px-4 py-6 sm:px-6">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-base font-semibold leading-6 text-white">
                {config.enableRightJSONModalPanelText}
              </Dialog.Title>
            </div>
            <div className="mt-1">
              <p className="text-sm text-primary-300">
                {config.enableRightJSONModalPanelText2}
              </p>
            </div>
          </div>
          <div className="relative flex-1 px-4 py-6 sm:px-6 w-full">
            {error && <p>Error: {error}</p>}
            {!error && (
              <>
                <div className={"flex"}>
                  <div className="border-b-0 border-gray-200 my-4 w-auto">
                    <nav aria-label="Tabs" className="-mb-px flex space-x-4">
                      {planTypes.map((planType) => (
                        <a
                          key={"planType-" + planType}
                          href={"#"}
                          onClick={(e) => {
                            e.preventDefault();
                            if (planType !== currentPlanType)
                              setSearchKeyword("");
                            setCurrentPlanType(planType);
                          }}
                          aria-current={
                            planType === currentPlanType ? "page" : undefined
                          }
                          className={classNames(
                            planType === currentPlanType
                              ? "border-primary-700 text-primary-700"
                              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                            "whitespace-nowrap border-2 px-2 py-2 text-sm font-medium rounded-md"
                          )}
                        >
                          {planType}
                        </a>
                      ))}
                    </nav>
                  </div>
                </div>
                <form
                  className="bg-gray-200 p-2 mb-2 rounded-md relative flex flex-1"
                  action="#"
                  method="GET"
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <label htmlFor="search-field" className="sr-only">
                    Search
                  </label>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="pointer-events-none absolute inset-y-0 left-2 h-full w-5 text-search-input-text-color"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                  <input
                    id="search-field"
                    className="bg-transparent block h-full w-full border-0 py-1 pl-8 pr-0 text-search-input-text-color placeholder:text-search-input-text-color/50 focus:ring-0 sm:text-sm outline-none  focus:outline-none"
                    placeholder={config.searchRightJSONModalPanelPlaceholder}
                    type="text"
                    name="search"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                  />

                  {searchKeyword && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      onClick={() => setSearchKeyword("")}
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="cursor-pointer absolute inset-y-0 right-2 h-full w-5 text-search-input-text-color"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </form>
              </>
            )}
            <div>
              <div className="space-y-2" ref={divRef}>
                {(
                  plans.find(
                    (planType) => planType.planType === currentPlanType
                  )?.properties ?? []
                )
                  .filter((plan: Plan) =>
                    searchKeyword.trim().length < minimunSearchKeyworkLength
                      ? true
                      : foundProperties[plan.propertyNumber] !== undefined
                  )
                  .map((plan) => (
                    <div
                      key={"propertyNumber-" + plan.propertyNumber}
                      onClick={() => setSelected(plan)}
                      className={
                        (selected?.propertyNumber === plan.propertyNumber ||
                          searchKeyword.trim().length >=
                          minimunSearchKeyworkLength
                          ? "border-primary-600  ring-primary-600"
                          : "border-gray-300") +
                        "relative block cursor-pointer rounded-lg border-2 bg-white px-6 py-4 shadow-sm focus:outline-none w-full"
                      }
                    >
                      <div className="sm:flex sm:justify-between ">
                        <div className="flex items-start gap-2 flex-1 justify-between">
                          <span className="flex flex-col text-sm">
                            <span className="font-medium text-gray-900">
                              {plan.propertyName}
                            </span>
                            <span className="text-gray-500 flex">
                              <span className="block sm:inline">
                                {plan.propertyNumber}
                              </span>
                            </span>
                          </span>

                          <div className="flex items-center">
                          {config.enableTiledViews &&
                            (selected?.propertyNumber === plan.propertyNumber ||
                              searchKeyword.trim().length >=
                              minimunSearchKeyworkLength) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleOpenTiledViews(plan);
                                }}
                                className={`
                                  inline-flex items-center gap-x-1.5 rounded-md bg-[#e5e7eb] text-[#727272] px-2.5 py-2 text-sm font-semibold shadow-sm hover:bg-[#d8dadd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 mr-2
                                  ${config.helperMethodsActionSize === "small"
                                    ? " text-xs my-1 mr-1 py-[2px] px-1"
                                    : config.helperMethodsActionSize === "large"
                                      ? " text-base my-2 mr-2 py-2 px-3"
                                      : " text-sm my-2 mr-2 py-1 px-3"
                                  }
                                `}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-6"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3.75 6A2.25 2.25 0 016 3.75h12A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6zM3.75 9h16.5M3.75 13.5h16.5M3.75 17.25h16.5"
                                  />
                                </svg>
                                <span>{config.tiledViewsButtonText || "Tiled View"}</span>
                              </button>
                            )}
                          {config.enableJsonPDFReport &&
                            (selected?.propertyNumber === plan.propertyNumber ||
                              searchKeyword.trim().length >=
                              minimunSearchKeyworkLength) && (
                              <button
                                onClick={() => {
                                  setOpenQuickPdfPrint({
                                    open: true,
                                    propertyName: plan.propertyName,
                                    propertyNumber: plan.propertyNumber,
                                    plan: plan,
                                    plans: plan.plans,
                                  });
                                }}
                                className={`
																inline-flex items-center gap-x-1.5 rounded-md bg-[#e5e7eb] text-[#727272] px-2.5 py-2 text-sm font-semibold shadow-sm hover:bg-[#e5e7eb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 mr-2
																${config.helperMethodsActionSize === "small"
                                    ? " text-xs my-1 mr-1 py-[2px] px-1"
                                    : config.helperMethodsActionSize === "large"
                                      ? " text-base my-2 mr-2 py-2 px-3"
                                      : " text-sm my-2 mr-2 py-1 px-3"
                                  }
														`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-6"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
                                  />
                                </svg>
                                <span>{config.enableJsonPDFReportText}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <span className="mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right min-w-[40px]">
                          <span className="font-medium text-gray-900">
                            {plan.plans.length}
                          </span>
                          <span className="ml-1 text-gray-500 sm:ml-0">
                            {config.planTypeText}
                            {plan.plans.length > 1 ? "s" : ""}
                          </span>
                        </span>
                      </div>

                      <div
                        className={
                          selected?.propertyNumber === plan.propertyNumber ||
                            searchKeyword.trim().length >=
                            minimunSearchKeyworkLength
                            ? "h-auto overflow-hidden transition-all"
                            : "h-auto max-h-0 overflow-hidden transition-all"
                        }
                      >
                        {plan.plans
                          .filter((planDetail) =>
                            searchKeyword.trim().length <
                              minimunSearchKeyworkLength
                              ? true
                              : foundProperties[plan.propertyNumber] !==
                              undefined &&
                              foundProperties[plan.propertyNumber][
                              planDetail.planName
                              ] !== undefined
                          )
                          .map((planDetail) => {
                            return (
                              <div
                                key={
                                  plan.propertyNumber +
                                  "planName-" +
                                  planDetail.planName
                                }
                                className={"mt-4 flex gap-2"}
                              >
                                <div
                                  className={
                                    "w-full flex-1 border border-gray-300 rounded-lg px-2 pt-2"
                                  }
                                >
                                  <div
                                    key={"planName" + planDetail.planName}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      selectFloorPlan(
                                        plan,
                                        planDetail,
                                        undefined
                                      );
                                    }}
                                    className="flex items-center"
                                  >
                                    <div className="flex flex-1 flex-col text-sm">
                                      <span className="font-medium text-gray-900">
                                        {">"} {planDetail.planName}
                                      </span>
                                      <span className="text-gray-500">
                                        <span className="block sm:inline">
                                          {planDetail.floorName}
                                        </span>
                                      </span>
                                      <span className="text-gray-300">
                                        <span className="block sm:inline">
                                          {planDetail.svgFileName.replace(
                                            ".svg",
                                            ""
                                          )}
                                        </span>
                                      </span>
                                    </div>
                                    <span className="mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                                      <span className="font-medium text-gray-900">
                                        {planDetail.planType}
                                      </span>
                                      <span className="ml-1 text-gray-500 sm:ml-0">
                                        {config.planSubTypeText}
                                      </span>
                                    </span>
                                  </div>
                                  {!(
                                    searchKeyword.trim().length >=
                                    minimunSearchKeyworkLength &&
                                    foundProperties[plan.propertyNumber] !==
                                    undefined &&
                                    foundProperties[plan.propertyNumber][
                                    planDetail.planName
                                    ] !== undefined &&
                                    (
                                      foundProperties[plan.propertyNumber][
                                        planDetail.planName
                                      ].spaces ?? []
                                    ).length > 0
                                  ) && <div className="mb-2" />}
                                  {searchKeyword.trim().length >=
                                    minimunSearchKeyworkLength &&
                                    foundProperties[plan.propertyNumber] !==
                                    undefined &&
                                    foundProperties[plan.propertyNumber][
                                    planDetail.planName
                                    ] !== undefined &&
                                    (
                                      foundProperties[plan.propertyNumber][
                                        planDetail.planName
                                      ].spaces ?? []
                                    ).length > 0 && (
                                      <PlanSpaceList
                                        expandedPlanSpaces={expandedPlanSpaces}
                                        plan={plan}
                                        planDetail={planDetail}
                                        foundProperties={foundProperties}
                                        width={width}
                                        element={(space: Space) => (
                                          <a
                                            key={
                                              planDetail.planName +
                                              "-space-" +
                                              space.spaceNumber +
                                              "-" +
                                              space.spaceHeader
                                            }
                                            href={"#"}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              selectFloorPlan(
                                                plan,
                                                planDetail,
                                                space
                                              );
                                            }}
                                            className={classNames(
                                              "hover:border-primary-700 hover:text-primary-700",
                                              "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                                              "whitespace-nowrap border-2 px-2 py-2 text-xs font-normal rounded-sm cursor-pointer"
                                            )}
                                          >
                                            {space.spaceHeader} (#
                                            {space.spaceNumber})
                                          </a>
                                        )}
                                        onClick={() => {
                                          setExpandedPlanSpaces({
                                            ...expandedPlanSpaces,
                                            [plan.propertyNumber +
                                              "-" +
                                              planDetail.planName]:
                                              !expandedPlanSpaces[
                                              plan.propertyNumber +
                                              "-" +
                                              planDetail.planName
                                              ],
                                          });
                                        }}
                                      />
                                    )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Panel>
    );
  };

  const renderOpacityContent = () => {
    return (
      <Panel
        order={config.actionPanelSide === "left" ? 2 : 1}
        maxSize={isMobileOrTablet ? 20 : undefined}
        className="opacity-40 bg-black"
      ></Panel>
    );
  };

  return (
    <div>
      <Transition show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          style={{ zIndex: 10000000 }}
          onClose={setOpen}
        >
          <div
            className="fixed inset-0 overflow-hidden min-w-[300px]"
            style={{ zIndex: 10000001 }}
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full">
                <DialogPanel
                  className={"pointer-events-auto relative w-96 "}
                  style={{ width: "100%", maxWidth: "100%" }}
                >
                  <PanelGroup
                    autoSaveId="plan-json-content-panel"
                    direction="horizontal"
                  >
                    {config.actionPanelSide === "left"
                      ? renderPanelContent()
                      : renderOpacityContent()}
                    {/* @ts-ignore */}
                    <PanelResizeHandle className="w-2 bg-gray-300 relative">
                      <div
                        className="h-8 w-4 bg-gray-300 flex items-center justify-center"
                        style={{
                          zIndex: 1000,
                          position: "absolute",
                          bottom: 0,
                          top: "calc(50% - 16px)",
                          left: config.actionPanelSide === "left" ? "auto" : -8,
                          right:
                            config.actionPanelSide === "left" ? -8 : "auto",
                        }}
                      >
                        <svg
                          width="20px"
                          height="32px"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g id="Interface / Drag_Horizontal">
                            <g id="Vector">
                              <path
                                d="M14 18C14 18.5523 14.4477 19 15 19C15.5523 19 16 18.5523 16 18C16 17.4477 15.5523 17 15 17C14.4477 17 14 17.4477 14 18Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 18C8 18.5523 8.44772 19 9 19C9.55228 19 10 18.5523 10 18C10 17.4477 9.55228 17 9 17C8.44772 17 8 17.4477 8 18Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 12C14 12.5523 14.4477 13 15 13C15.5523 13 16 12.5523 16 12C16 11.4477 15.5523 11 15 11C14.4477 11 14 11.4477 14 12Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 12C8 12.5523 8.44772 13 9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 6C14 6.55228 14.4477 7 15 7C15.5523 7 16 6.55228 16 6C16 5.44772 15.5523 5 15 5C14.4477 5 14 5.44772 14 6Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 6C8 6.55228 8.44772 7 9 7C9.55228 7 10 6.55228 10 6C10 5.44772 9.55228 5 9 5C8.44772 5 8 5.44772 8 6Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                          </g>
                        </svg>
                      </div>
                    </PanelResizeHandle>
                    {config.actionPanelSide !== "left"
                      ? renderPanelContent()
                      : renderOpacityContent()}
                  </PanelGroup>
                </DialogPanel>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {config.enableTiledViews && (
        <TiledViewsWindow
          open={tiledViewsOpen}
          onClose={() => setTiledViewsOpen(false)}
          data={tiledViewsData}
          loading={tiledViewsLoading}
          error={tiledViewsError}
          title={config.tiledViewsModalTitle || "Tower Overview"}
          subtitle={
            tiledViewsPlan
              ? `${tiledViewsPlan.propertyName} · ${tiledViewsPlan.propertyNumber}`
              : undefined
          }
          activeFloorName={activeTiledFloorName}
          onSelectFloor={handleSelectFloorByName}
        />
      )}

      {config.enableJsonPDFReport && openQuickPdfPrint.open && (
        <QuickPrintPdf
          open={openQuickPdfPrint.open}
          propertyName={openQuickPdfPrint.propertyName}
          propertyNumber={openQuickPdfPrint.propertyNumber}
          plan={openQuickPdfPrint}
          plans={openQuickPdfPrint.plans}
          setOpen={(open: boolean) => {
            setOpenQuickPdfPrint((prev) => ({ ...prev, open }));
          }}
        />
      )}
    </div>
  );
};

export default PlanJsonRightPanel;
